import React from "react";
import { Link } from "react-router-dom";
import { PageHero, Section, Eyebrow, SectionTitle } from "../components/Primitives";
import { Users, Globe, HeartPulse, Sprout } from "lucide-react";

const img1 = "https://images.unsplash.com/photo-1609234656381-73e732808098?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBnYXRoZXJpbmd8ZW58MHx8fHwxNzg4MjQ1MTE0fDA&ixlib=rb-4.1.0&q=85";
const img2 = "https://images.unsplash.com/photo-1616901945149-47f7eac169cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxidWRkaGlzdCUyMHRlbXBsZXxlbnwwfHx8fDE3ODgyNDUxMTR8MA&ixlib=rb-4.1.0&q=85";

const Community = () => (
  <>
    <PageHero
      eyebrow="Community Outreach"
      title="Small acts, patiently repeated."
      subtitle="Our outreach is quiet and steady — shared meals, health workshops, disaster support, and long-term partnerships with the communities who need us most."
    />

    <Section>
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div className="space-y-6 text-[17px] text-[#4a3a2a] leading-relaxed">
          <Eyebrow>Who we walk alongside</Eyebrow>
          <SectionTitle>Everyone the ACNC asks us to serve — and everyone else.</SectionTitle>
          <p>Our charity register lists eighteen groups we work with. In practice it means we open our gates to whoever knocks: elders and children, families and single parents, refugees and long-time locals, LGBTIQA+ neighbours, people in rural towns, and those quietly doing it tough.</p>
          <p>We keep our services free of jargon and free of judgement. If you or someone you love needs a warm meal, a safe bed, a friendly ear, or simply a place to breathe — you belong here.</p>
        </div>
        <div className="relative aspect-[5/6] overflow-hidden">
          <img src={img1} alt="Community members sharing food and conversation" className="w-full h-full object-cover" loading="lazy" />
        </div>
      </div>
    </Section>

    <section className="bg-[#f6efe0] border-y border-[#e9dcc0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-14">
          <Eyebrow>Where our work reaches</Eyebrow>
          <SectionTitle className="mt-6">Local roots, careful international support.</SectionTitle>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { Icon: Users, t: "Canberra & ACT", d: "Our home community — weekly programs, family support and cultural events." },
            { Icon: Globe, t: "Vietnam", d: "Rural school kitchens, medical supplies and elder care through trusted local partners." },
            { Icon: HeartPulse, t: "India & Nepal", d: "Modest support for pilgrimage sites, monastic education and disaster relief." },
            { Icon: Sprout, t: "Regional Australia", d: "Quiet visits and modest grants to rural families affected by fire and flood." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="bg-[#fdfaf3] p-8 border border-[#e9dcc0]">
              <Icon size={26} className="text-[#7a2e1f]" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl text-[#3d2817] mt-5">{t}</h3>
              <p className="mt-3 text-[15px] text-[#5a4a3a] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <Section>
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative aspect-[5/6] overflow-hidden order-2 lg:order-1">
          <img src={img2} alt="Temple lanterns glowing in the evening" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="order-1 lg:order-2">
          <Eyebrow>Recent partnerships</Eyebrow>
          <SectionTitle>Working with people who know their community best.</SectionTitle>
          <ul className="mt-8 space-y-6">
            {[
              { t: "Hepatitis ACT · LiverWise", d: "In-language health education and hepatitis B screening for the Vietnamese-speaking community." },
              { t: "Multicultural Hub Canberra", d: "Shared meals, translated resources and settlement support for newly arrived families." },
              { t: "Australian Buddhist Relief", d: "Coordinated disaster response and modest overseas relief through trusted networks." },
              { t: "Van Lang Village for the Aged", d: "Regular visits, chaplaincy and cultural programming for Vietnamese elders in care." },
            ].map((p) => (
              <li key={p.t} className="border-l-2 border-[#c9a15b] pl-5">
                <div className="font-serif text-xl text-[#3d2817]">{p.t}</div>
                <p className="mt-1 text-[15px] text-[#5a4a3a] leading-relaxed">{p.d}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>

    <section className="bg-[#7a2e1f] text-[#f6efe0]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
        <h2 className="font-serif text-3xl md:text-4xl leading-tight text-[#f6efe0]">Volunteer with us. Share a Sunday. Cook a pot.</h2>
        <p className="mt-5 text-[#e6d9be]/90 max-w-2xl mx-auto">Our work runs on kind hands and warm hearts. If you have a spare morning, a skill, or simply the willingness to sit and listen, we’d be grateful to have you.</p>
        <Link to="/contact" className="inline-flex mt-8 items-center gap-2 bg-[#c9a15b] text-[#3d2817] px-7 py-3 hover:bg-[#e0b970] transition-colors duration-200 font-medium">Get involved</Link>
      </div>
    </section>
  </>
);

export default Community;
